import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { BellRing, Calculator, ChefHat, Clock, Pause, Play, Volume2, VolumeX, Wifi, WifiOff } from "lucide-react";
import { ORDER_TRANSITIONS, type OrderDto, type OrderStatus } from "@dishfolio/contracts";
import { Badge, Button, Card, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, EmptyState, Input, NativeSelect, cn, toast } from "@dishfolio/ui";
import { api } from "../lib/api.ts";
import { chime, useLiveOrders } from "../lib/live-orders.ts";
import { useOrg } from "../lib/org.tsx";
import { money, relativeTime } from "../lib/format.ts";
import { useRestaurants, useTables } from "../lib/queries.ts";
import { PageContainer, PageHeader } from "../components/Page.tsx";
import { notifyError, UpgradeCard } from "../components/Upgrade.tsx";

const ACTION_LABEL: Partial<Record<OrderStatus, string>> = {
  accepted: "Accept",
  preparing: "Start preparing",
  ready: "Mark ready",
  served: "Served",
  rejected: "Reject",
  cancelled: "Cancel",
};

const STATUS_BADGE: Record<OrderStatus, "default" | "secondary" | "success" | "warning" | "destructive" | "outline"> = {
  placed: "warning",
  accepted: "secondary",
  preparing: "secondary",
  ready: "success",
  served: "outline",
  rejected: "destructive",
  cancelled: "destructive",
};

function OrderCard({ order, onAction, fresh }: { order: OrderDto; onAction: (o: OrderDto, s: OrderStatus) => void; fresh: boolean }) {
  const next = ORDER_TRANSITIONS[order.status];
  const forward = next.filter((s) => s !== "rejected" && s !== "cancelled");
  const negative = next.filter((s) => s === "rejected" || s === "cancelled");
  return (
    <Card className={cn("gap-3 p-4", fresh && "animate-pulse ring-2 ring-primary")}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 text-lg font-semibold">
            #{order.number}
            <Badge variant={STATUS_BADGE[order.status]} className="capitalize">
              {order.status}
            </Badge>
          </div>
          <div className="text-sm text-muted-foreground">
            {order.tableId ? `Table ${order.tableLabel}` : (order.tableLabel ?? "No table")} · <Clock className="inline size-3" /> {relativeTime(order.createdAt)}
            {order.source === "pos" ? " · POS" : ""}
            {order.settledAt ? " · closed" : ""}
          </div>
        </div>
        <div className="text-right font-semibold tabular-nums">{money(order.totalMinor, order.currency)}</div>
      </div>
      <ul className="grid gap-1.5 text-sm">
        {order.items.map((i) => (
          <li key={i.id} className={cn(i.voidedAt && "text-muted-foreground line-through")} title={i.voidReason ? `Voided: ${i.voidReason}` : undefined}>
            <div className="flex justify-between gap-2">
              <span>
                <span className="font-semibold">{i.quantity}×</span> {i.name}
                {i.variantName ? <span className="text-muted-foreground"> ({i.variantName})</span> : null}
              </span>
            </div>
            {i.modifiers.length ? <div className="pl-5 text-xs text-muted-foreground">+ {i.modifiers.map((m) => m.optionName).join(", ")}</div> : null}
            {i.notes ? <div className="pl-5 text-xs font-medium italic">“{i.notes}”</div> : null}
          </li>
        ))}
      </ul>
      {order.notes ? <div className="rounded-md bg-accent px-2.5 py-1.5 text-sm">Note: {order.notes}</div> : null}
      {next.length ? (
        <div className="flex flex-wrap gap-2">
          {forward.map((s) => (
            <Button key={s} size="sm" onClick={() => onAction(order, s)} className="flex-1">
              {ACTION_LABEL[s]}
            </Button>
          ))}
          {negative.map((s) => (
            <Button key={s} size="sm" variant="ghost" className="text-destructive" onClick={() => onAction(order, s)}>
              {ACTION_LABEL[s]}
            </Button>
          ))}
        </div>
      ) : (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer">History</summary>
          <ul className="mt-1 grid gap-0.5">
            {order.history.map((h, i) => (
              <li key={i}>
                {new Date(h.at).toLocaleTimeString()} — {h.to}
                {h.actor ? ` by ${h.actor}` : ""}
                {h.reason ? ` (${h.reason})` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}

export function OrdersPage() {
  const search = useSearch({ from: "/app/orders" });
  const navigate = useNavigate();
  const { org, orgId, can, feature } = useOrg();
  const restaurants = useRestaurants();
  const qc = useQueryClient();
  const restaurantId = search.restaurantId ?? restaurants.data?.[0]?.id;
  const r = restaurants.data?.find((x) => x.id === restaurantId);
  const tables = useTables(restaurantId);
  const [tableFilter, setTableFilter] = useState("");
  const [sound, setSound] = useState(true);
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  const [reasonFor, setReasonFor] = useState<{ order: OrderDto; status: OrderStatus } | null>(null);
  const [reason, setReason] = useState("");
  const soundRef = useRef(sound);
  soundRef.current = sound;
  const { orders, live, upsert } = useLiveOrders(restaurantId, {
    enabled: Boolean(orgId && org?.plan.tableOrdering),
    onNew: (o) => {
      setFresh((s) => new Set(s).add(o.id));
      setTimeout(() => setFresh((s) => (s.delete(o.id), new Set(s))), 8000);
      if (soundRef.current) chime();
      toast(`New order #${o.number}${o.tableLabel ? ` · Table ${o.tableLabel}` : ""}`, { icon: <BellRing className="size-4" /> });
      if (document.hidden && "Notification" in window && Notification.permission === "granted") new Notification(`New order #${o.number}`, { body: o.tableLabel ? `Table ${o.tableLabel}` : undefined });
    },
  });

  useEffect(() => {
    const pending = (orders.data ?? []).filter((o) => o.status === "placed").length;
    document.title = pending ? `(${pending}) New orders · Dishfolio` : "Orders · Dishfolio";
    return () => void (document.title = "Dishfolio");
  }, [orders.data]);

  if (org && !org.plan.tableOrdering) {
    return (
      <PageContainer>
        <PageHeader title="Orders" />
        <UpgradeCard plan="Business" title="Table ordering" description="Guests order from their table; your staff accepts and tracks orders here in real time." />
      </PageContainer>
    );
  }
  const act = async (o: OrderDto, status: OrderStatus, why?: string) => {
    if ((status === "rejected" || status === "cancelled") && !why) {
      setReasonFor({ order: o, status });
      setReason("");
      return;
    }
    try {
      const res = await api.patch<{ order: OrderDto }>(`/orders/${o.id}/status`, { status, reason: why });
      upsert(res.order);
    } catch (err) {
      notifyError(err);
      void orders.refetch();
    }
  };
  const list = (orders.data ?? []).filter((o) => !tableFilter || o.tableId === tableFilter);
  const columns: Array<{ title: string; statuses: OrderStatus[] }> = [
    { title: "New", statuses: ["placed"] },
    { title: "In progress", statuses: ["accepted", "preparing"] },
    { title: "Ready", statuses: ["ready"] },
  ];
  const done = list.filter((o) => ["served", "rejected", "cancelled"].includes(o.status));
  return (
    <PageContainer wide>
      <PageHeader
        title="Orders"
        description={r ? r.name : undefined}
        actions={
          <>
            <span className={cn("flex items-center gap-1.5 text-xs", live ? "text-success" : "text-muted-foreground")}>
              {live ? <Wifi className="size-3.5" /> : <WifiOff className="size-3.5" />} {live ? "Live" : "Reconnecting… (polling)"}
            </span>
            {restaurants.data && restaurants.data.length > 1 ? (
              <NativeSelect className="w-auto" value={restaurantId} onChange={(e) => navigate({ to: "/orders", search: { restaurantId: e.target.value } })}>
                {restaurants.data.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </NativeSelect>
            ) : null}
            <NativeSelect className="w-auto" value={tableFilter} onChange={(e) => setTableFilter(e.target.value)} aria-label="Filter by table">
              <option value="">All tables</option>
              {tables.data?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </NativeSelect>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                setSound(!sound);
                if (!sound) chime();
                if ("Notification" in window && Notification.permission === "default") void Notification.requestPermission();
              }}
              aria-label={sound ? "Mute alerts" : "Unmute alerts"}
            >
              {sound ? <Volume2 /> : <VolumeX />}
            </Button>
            {can("orders:manage") && feature("pos") ? (
              <Button asChild variant="outline">
                <Link to="/pos" search={{ restaurantId }}>
                  <Calculator /> POS
                </Link>
              </Button>
            ) : null}
            <Button asChild variant="outline">
              <Link to="/kitchen" search={{ restaurantId }}>
                <ChefHat /> Kitchen display
              </Link>
            </Button>
            {r && can("orders:manage") ? (
              <Button
                variant={r.orderingPaused ? "default" : "outline"}
                onClick={async () => {
                  await api.patch(`/restaurants/${r.id}`, { orderingPaused: !r.orderingPaused });
                  await qc.invalidateQueries({ queryKey: ["restaurants"] });
                }}
              >
                {r.orderingPaused ? <Play /> : <Pause />} {r.orderingPaused ? "Resume ordering" : "Pause ordering"}
              </Button>
            ) : null}
          </>
        }
      />
      {r && !r.orderingEnabled ? <div className="mb-4 rounded-lg border bg-accent p-3 text-sm">Table ordering is switched off for this location. Turn it on in the location settings.</div> : null}
      {r?.orderingPaused ? <div className="mb-4 rounded-lg border border-warning/50 bg-warning/10 p-3 text-sm">Ordering is paused — guests can browse but not order.</div> : null}
      <div className="grid gap-5 lg:grid-cols-3">
        {columns.map((col) => {
          const items = list.filter((o) => col.statuses.includes(o.status)).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
          return (
            <section key={col.title} className="grid content-start gap-3" aria-label={col.title}>
              <h2 className="flex items-center gap-2 font-semibold">
                {col.title} <Badge variant="secondary">{items.length}</Badge>
              </h2>
              {items.length ? items.map((o) => <OrderCard key={o.id} order={o} onAction={act} fresh={fresh.has(o.id)} />) : <EmptyState className="p-6" title="Nothing here" />}
            </section>
          );
        })}
      </div>
      {done.length ? (
        <section className="mt-8 grid gap-3">
          <h2 className="font-semibold">Completed (last 12 hours)</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {done.slice(0, 30).map((o) => (
              <OrderCard key={o.id} order={o} onAction={act} fresh={false} />
            ))}
          </div>
        </section>
      ) : null}
      <Dialog open={Boolean(reasonFor)} onOpenChange={(o) => !o && setReasonFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{reasonFor?.status === "rejected" ? "Reject" : "Cancel"} order #{reasonFor?.order.number}</DialogTitle>
          </DialogHeader>
          <Input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason shown in history (e.g. sold out)" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReasonFor(null)}>
              Back
            </Button>
            <Button
              variant="destructive"
              disabled={!reason.trim()}
              onClick={async () => {
                if (reasonFor) await act(reasonFor.order, reasonFor.status, reason.trim());
                setReasonFor(null);
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
